import React, { useEffect, useState } from 'react';
import { useGetEventInvoicingReadinessQuery } from '../../../graphql/queries/getEventInvoicingReadiness.generated';
import { GetEventQuery } from '../../../graphql/queries/getEvent.generated';
import { EventType, IdName } from '../../../graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { Link } from 'react-router-dom';
import { FaLightbulb } from 'react-icons/fa';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { useCreateEventInvoicesMutation } from '../../../graphql/mutations/createEventInvoices.generated';
import { useAllItemTypesQuery } from '../../../graphql/queries/allItemTypes.generated';
import { distinct } from '@/utils/arrayUtils';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { Communicator } from '../../../tools/Communication';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Alert } from '@/components/ui/alert';

interface EventInvoicingTabProps {
  eventId: string;
  eventParticipants: NonNullable<
    NonNullable<GetEventQuery['event']>['event_participants']
  >;
  eventType: EventType;
  mainContractingEventAccount?: {
    id: string;
    name?: string | null;
    description?: string | null;
  } | null;
}

const EventInvoicingTab: React.FC<EventInvoicingTabProps> = ({
  eventId,
  eventParticipants,
  eventType,
  mainContractingEventAccount,
}) => {
  const { loading, error, data } = useGetEventInvoicingReadinessQuery({
    variables: { event_id: eventId },
    fetchPolicy: 'no-cache',
  });

  const [selectedEventAccountIds, setSelectedEventAccounts] = React.useState<
    string[]
  >(
    eventType === EventType.StandardPerGastronomist
      ? []
      : eventParticipants.map((ea) => ea.id),
  );
  const [allContainerTypes, setAllContainerTypes] = useState<IdName[]>([]);
  const { data: allItemTypes } = useAllItemTypesQuery({
    variables: { filter: {}, skip: 0, take: 500 },
  });

  const [createEventInvoices, { loading: creatingInvoices }] =
    useCreateEventInvoicesMutation({
      variables: {
        event_id: eventId,
        event_account_ids:
          eventType !== EventType.StandardPerGastronomist
            ? []
            : selectedEventAccountIds,
      },
      onError: (error) => {
        console.error(error);
        LayoutStoreInstance.setError(
          i18n.t('EventInvoicing:invoiceGenerationFailed') +
            ': ' +
            error.message,
        );
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess(
          i18n.t('EventInvoicing:invoiceGenerationSuccess'),
        );
        setSelectedEventAccounts(
          eventType === EventType.StandardPerGastronomist
            ? []
            : eventParticipants.map((ea) => ea.id),
        );
      },
    });

  useEffect(() => {
    setSelectedEventAccounts(
      eventType === EventType.StandardPerGastronomist
        ? []
        : eventParticipants.map((ea) => ea.id),
    );
  }, [eventParticipants, eventType]);

  useEffect(() => {
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: IdName[] }) =>
        setAllContainerTypes(data.containerTypes),
      );
  }, []);

  async function toggleAllEventAccounts(eventAccountIds: string[]) {
    if (eventAccountIds.every((id) => selectedEventAccountIds.includes(id))) {
      setSelectedEventAccounts([]);
    } else {
      setSelectedEventAccounts([...eventAccountIds]);
    }
  }

  function isEventAccountReadyForInvoicing(eventAccountId: string) {
    const readiness = data?.getEventInvoicingReadiness?.find(
      (ea) => ea.event_account_id === eventAccountId,
    );
    return (
      readiness &&
      readiness.missing_prices_container_type_ids.length === 0 &&
      readiness.not_allowed_container_type_ids.length === 0 &&
      readiness.uncounted_container_station_ids.length === 0 &&
      readiness.missing_sku_container_type_ids.length === 0 &&
      readiness.wrong_class_container_type_ids.length === 0 &&
      readiness.missing_prices_item_type_ids.length === 0 &&
      readiness.not_allowed_item_type_ids.length === 0 &&
      readiness.missing_sku_item_type_ids.length === 0 &&
      !readiness.missing_main_contractor
    );
  }

  function itemTypeNames(ids: number[]): string {
    return ids
      .filter(distinct)
      .sort((a, b) => a - b)
      .map(
        (id) =>
          allItemTypes?.itemTypes?.items?.find((it) => it.id === id)?.name ??
          `#${id}`,
      )
      .join(', ');
  }

  function itemTypeProblems(readiness: {
    event_account_id: string;
    missing_prices_item_type_ids: number[];
    not_allowed_item_type_ids: number[];
    missing_sku_item_type_ids: number[];
  }) {
    const problems = [];
    if (readiness.missing_prices_item_type_ids.length > 0) {
      problems.push({
        eventAccountId: readiness.event_account_id,
        type: 'MissingItemTypePrices',
        info: itemTypeNames(readiness.missing_prices_item_type_ids),
      });
    }
    if (readiness.not_allowed_item_type_ids.length > 0) {
      problems.push({
        eventAccountId: readiness.event_account_id,
        type: 'ItemTypeNotAllowed',
        info: itemTypeNames(readiness.not_allowed_item_type_ids),
      });
    }
    if (readiness.missing_sku_item_type_ids.length > 0) {
      problems.push({
        eventAccountId: readiness.event_account_id,
        type: 'MissingItemTypeSku',
        info: itemTypeNames(readiness.missing_sku_item_type_ids),
      });
    }
    return problems;
  }

  function getInvoicingProblems(): {
    eventAccountId?: string;
    type: string;
    info: string;
  }[] {
    const problems: {
      eventAccountId?: string;
      type: string;
      info: string;
    }[] = (data?.getEventInvoicingReadiness || [])
      .filter(
        (ea) =>
          selectedEventAccountIds.includes(ea.event_account_id) &&
          !isEventAccountReadyForInvoicing(ea.event_account_id),
      )
      .flatMap((ea) => {
        const problems = [];
        if (ea.missing_prices_container_type_ids.length > 0) {
          problems.push({
            eventAccountId: ea.event_account_id,
            type: 'MissingPrices',
            info: ea.missing_prices_container_type_ids
              .filter(distinct)
              .sort((a, b) => a - b)
              .map(
                (id) =>
                  allContainerTypes.find((ct) => parseInt(ct.id) === id)?.name,
              )
              .join(', '),
          });
        }
        if (ea.not_allowed_container_type_ids.length > 0) {
          problems.push({
            eventAccountId: ea.event_account_id,
            type: 'NotAllowed',
            info: ea.not_allowed_container_type_ids
              .filter(distinct)
              .sort((a, b) => a - b)
              .map(
                (id) =>
                  allContainerTypes.find((ct) => parseInt(ct.id) === id)?.name,
              )
              .join(', '),
          });
        }
        if (ea.uncounted_container_station_ids.length > 0) {
          problems.push(
            ...ea.uncounted_container_station_ids.map((stationId) => ({
              eventAccountId: ea.event_account_id,
              type: 'UncountedStation',
              info: stationId,
            })),
          );
        }
        if (ea.missing_sku_container_type_ids.length > 0) {
          problems.push({
            eventAccountId: ea.event_account_id,
            type: 'MissingSku',
            info: ea.missing_sku_container_type_ids
              .filter(distinct)
              .sort((a, b) => a - b)
              .map(
                (id) =>
                  allContainerTypes.find((ct) => parseInt(ct.id) === id)?.name,
              )
              .join(', '),
          });
        }
        if (ea.wrong_class_container_type_ids.length > 0) {
          problems.push({
            eventAccountId: ea.event_account_id,
            type: 'WrongClass',
            info: ea.wrong_class_container_type_ids
              .filter(distinct)
              .sort((a, b) => a - b)
              .map(
                (id) =>
                  allContainerTypes.find((ct) => parseInt(ct.id) === id)?.name,
              )
              .join(', '),
          });
        }
        problems.push(...itemTypeProblems(ea));
        if (ea.missing_main_contractor) {
          problems.push({
            eventAccountId: ea.event_account_id,
            type: 'MissingMainContractor',
            info: '',
          });
        }
        return problems;
      });
    if (eventType === EventType.Default) {
      problems.push({
        type: 'EventTypeDefault',
        info: i18n.t('EventInvoicing:defaultEventTypeWarning'),
      });
    }
    return problems;
  }

  return (
    <div className="tw:space-y-6">
      <Button
        onClick={() => createEventInvoices()}
        disabled={
          creatingInvoices ||
          eventType === EventType.Default ||
          selectedEventAccountIds.length === 0 ||
          eventParticipants?.some(
            (ea) =>
              !isEventAccountReadyForInvoicing(ea.id) &&
              selectedEventAccountIds.includes(ea.id),
          )
        }
      >
        {i18n.t('EventInvoicing:generateInvoices', {
          count:
            eventType !== EventType.StandardPerGastronomist
              ? 1
              : selectedEventAccountIds.length,
        })}
      </Button>
      <div className="tw:space-y-4">
        <h3 className="tw:text-xl tw:font-semibold">
          {i18n.t('Event:eventAccounts')}
        </h3>
        <div>
          {eventType !== EventType.StandardPerGastronomist && (
            <div className="tw:p-3 tw:bg-yellow-50 tw:border tw:border-yellow-200 tw:rounded-md tw:text-yellow-800 tw:mb-4">
              <div className="tw:flex">
                <div className="tw:self-center">
                  <FaLightbulb />
                </div>
                <div className="tw:ml-2">
                  {i18n.t('EventInvoicing:notOnsiteDistributionHelpText')}
                </div>
              </div>
            </div>
          )}
          <div className="tw:mb-3">
            <h5 className="tw:text-lg tw:font-medium tw:mb-2">
              Main Contractor
            </h5>
            <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-background">
              {mainContractingEventAccount ? (
                <Link
                  to={'/event-account/' + mainContractingEventAccount.id}
                  className="tw:text-primary tw:hover:underline"
                >
                  {mainContractingEventAccount.name}
                </Link>
              ) : (
                <span className="tw:text-gray-500">
                  {i18n.t('Common:notSet')}
                </span>
              )}
            </div>
          </div>
          <h5 className="tw:text-lg tw:font-medium tw:mb-2">
            {i18n.t('EventAccount:subContractorsParticipatingEventAccounts')}
          </h5>
          {loading ? (
            <div className="tw:py-4 tw:text-center">
              <div
                className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
                role="status"
              >
                <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
              </div>
            </div>
          ) : error ? (
            <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
              {i18n.t('Common:error')}
            </div>
          ) : (
            <Card className="tw:overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="tw:*:first:pl-4 tw:*:last:pr-4 tw:*:last:py-4">
                    <TableHead>
                      <input
                        type="checkbox"
                        disabled={
                          eventType !== EventType.StandardPerGastronomist
                        }
                        checked={(eventParticipants || [])
                          .filter((ea) => ea.is_billable)
                          .map((ea) => ea.id)
                          .filter((id) => isEventAccountReadyForInvoicing(id))
                          .every((id) => selectedEventAccountIds.includes(id))}
                        onChange={() =>
                          toggleAllEventAccounts(
                            (eventParticipants || [])
                              .filter((ea) => ea.is_billable)
                              .map((ea) => ea.id)
                              .filter((id) =>
                                isEventAccountReadyForInvoicing(id),
                              ),
                          )
                        }
                        className="tw:w-4 tw:h-4"
                      />
                    </TableHead>
                    <TableHead>{i18n.t('Common:name')}</TableHead>
                    <TableHead>{i18n.t('EventAccount:isBillable')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventParticipants?.map((ea) => (
                    <TableRow
                      key={ea.id}
                      className={cn(
                        'tw:*:first:pl-4 tw:*:last:pr-4 tw:*:last:py-4',
                        !isEventAccountReadyForInvoicing(ea.id) &&
                          'tw:bg-yellow-50! tw:dark:bg-yellow-600/20!',
                      )}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          disabled={
                            eventType !== EventType.StandardPerGastronomist ||
                            !ea.is_billable
                          }
                          checked={selectedEventAccountIds.includes(ea.id)}
                          onChange={() =>
                            setSelectedEventAccounts(() => {
                              if (selectedEventAccountIds.includes(ea.id)) {
                                return selectedEventAccountIds.filter(
                                  (id) => id !== ea.id,
                                );
                              }
                              return selectedEventAccountIds.concat([ea.id]);
                            })
                          }
                          className="tw:w-4 tw:h-4"
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={'/event-account/' + ea.id}
                          className="tw:text-primary tw:hover:underline"
                        >
                          {ea.name || ea.email || ea.id}
                        </Link>
                        {mainContractingEventAccount?.id === ea.id && (
                          <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:rounded tw:bg-primary-dark tw:text-white tw:ml-2">
                            Main Contractor
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {ea.is_billable
                          ? i18n.t('Common:yes')
                          : i18n.t('Common:no')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
      <div className="tw:space-y-4">
        <h3 className="tw:text-xl tw:font-semibold">
          {i18n.t('EventInvoicing:problems', {
            count: getInvoicingProblems().length,
          })}
        </h3>
        {loading ? (
          <div className="tw:py-4 tw:text-center">
            <div
              className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
              role="status"
            >
              <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
            {i18n.t('Common:error')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n.t('EventInvoicing:eventAccount')}</TableHead>
                <TableHead>{i18n.t('EventInvoicing:problemType')}</TableHead>
                <TableHead>{i18n.t('EventInvoicing:detail')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getInvoicingProblems().map((problem, i) => (
                <TableRow key={problem.eventAccountId + '-' + i}>
                  <TableCell>
                    {problem.eventAccountId ? (
                      <Link
                        to={'/event-account/' + problem.eventAccountId}
                        className="tw:text-primary tw:hover:underline"
                      >
                        {
                          eventParticipants.find(
                            (ea) => ea.id === problem.eventAccountId,
                          )?.name
                        }
                      </Link>
                    ) : (
                      <span className="tw:text-gray-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {i18n.t(`EventInvoicing:problemType${problem.type}`)}
                  </TableCell>
                  <TableCell>
                    {problem.type !== 'UncountedStation' && problem.info}
                    {problem.type === 'UncountedStation' && (
                      <Link
                        to={'/container-station/' + problem.info}
                        className="tw:text-primary tw:hover:underline"
                      >
                        {problem.info}
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default EventInvoicingTab;
